import { supabase } from './supabase';
import { Character2DLayered, CharacterPassport } from '../types';
import { uploadToR2, deleteFromR2, copyInR2 } from './storage';

/** Retry an idempotent upsert only when the connection failed, never a database rejection. */
export async function retryCharacterWrite<T>(write: () => Promise<T>,
  wait: (milliseconds: number) => Promise<void> = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds))
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try { return await write(); }
    catch (error) {
      const details = error && typeof error === 'object' ? error as { code?: string; message?: string; details?: string; name?: string } : {};
      const transient = !details.code && (details.name === 'AbortError' || details.name === 'TimeoutError' ||
        /failed to fetch|fetch failed|network|connection|timed?\s*out|timeout|aborted/i.test(`${details.message || ''} ${details.details || ''}`));
      if (!transient || attempt >= 2) throw error;
      await wait(1000 * (attempt + 1));
    }
  }
}

// ============================================
// IMAGE COMPRESSION UTILITIES
// ============================================

/**
 * Compress an image blob for web-ready display
 * Creates a smaller JPEG version optimized for fast loading
 * @param blob - Original image blob
 * @param maxWidth - Maximum width (default 800px for web display)
 * @param quality - JPEG quality 0-1 (default 0.75)
 * @returns Compressed blob as JPEG
 */
export async function compressImageForWeb(
  blob: Blob,
  maxWidth: number = 800,
  quality: number = 0.75
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to JPEG blob
      canvas.toBlob(
        (compressedBlob) => {
          if (compressedBlob) {
            console.log(`🗜️ Compressed: ${(blob.size / 1024).toFixed(0)}KB → ${(compressedBlob.size / 1024).toFixed(0)}KB (${((1 - compressedBlob.size / blob.size) * 100).toFixed(0)}% smaller)`);
            resolve(compressedBlob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = URL.createObjectURL(blob);
  });
}

// ============================================
// R2 STORAGE HELPERS
// ============================================

/**
 * Upload an image blob to R2 with full path control
 * Path structure: ezart-assets/character/{temp|final}/{projectId}/{characterId}/images/{filename}
 */
export async function uploadCharacterImageToR2(
  blob: Blob,
  userId: string,
  filename: string,
  folderPath: string
): Promise<string> {
  // Convert blob to File
  const file = new File([blob], filename, { type: blob.type });

  // Upload to R2 with the provided path
  const url = await uploadToR2(file, folderPath, userId);
  return url;
}

/**
 * Upload temporary image during character creation (editing phase)
 * Saved to: ezart-assets/character/temp/{projectId}/{characterId}/images/
 */
export async function uploadTempCharacterImage(
  blob: Blob,
  userId: string,
  projectId: string,
  characterId: string,
  filename: string
): Promise<string> {
  const path = `character/temp/${projectId}/${characterId}/images`;
  return uploadCharacterImageToR2(blob, userId, filename, path);
}

/**
 * Upload permanent image after character finalization
 * Saved to: character/final/{projectId}/{characterId}/images/
 */
export async function uploadPermanentCharacterImage(
  blob: Blob,
  userId: string,
  projectId: string,
  characterId: string,
  filename: string
): Promise<string> {
  const path = `character/final/${projectId}/${characterId}/images`;
  return uploadCharacterImageToR2(blob, userId, filename, path);
}

/**
 * Copy character images to final storage. Cleanup must follow successful database saves.
 * Also creates web-optimized compressed versions for fast loading
 *
 * @param tempUrls - Object containing temp URLs for body, head, transposed, nsfw
 * @param userId - User ID
 * @param projectId - Project ID
 * @param characterId - Character ID
 * @param blobs - Optional object containing blobs to use directly (bypasses CORS fetch)
 * @returns Object with both original and web-optimized URLs
 */
export async function finalizeCharacterImages(
  tempUrls: {
    bodyUrl?: string;
    headUrl?: string;
    transposedBodyUrl?: string;
    nsfwUrl?: string;
    referenceSheetUrl?: string;
    unclothedReferenceSheetUrl?: string;
  },
  userId: string,
  projectId: string,
  characterId: string,
  blobs?: {
    bodyBlob?: Blob;
    headBlob?: Blob;
    transposedBodyBlob?: Blob;
    nsfwBlob?: Blob;
  }
): Promise<{
  bodyUrl?: string;
  headUrl?: string;
  transposedBodyUrl?: string;
  nsfwUrl?: string;
  referenceSheetUrl?: string;
  unclothedReferenceSheetUrl?: string;
  // Web-optimized compressed versions for fast loading
  webBodyUrl?: string;
  webHeadUrl?: string;
  webTransposedUrl?: string;
  tempUrlsToDelete: string[];
}> {
  const finalUrls: {
    bodyUrl?: string;
    headUrl?: string;
    transposedBodyUrl?: string;
    nsfwUrl?: string;
    referenceSheetUrl?: string;
    unclothedReferenceSheetUrl?: string;
    webBodyUrl?: string;
    webHeadUrl?: string;
    webTransposedUrl?: string;
  } = {};

  const tempUrlsToDelete: string[] = [];
  const finalPath = `character/final/${projectId}/${characterId}/images`;

  // Helper to move a single image from temp to final and optionally create compressed version
  const moveToFinal = async (
    tempUrl: string,
    filename: string,
    existingBlob?: Blob,
    createWebVersion: boolean = false
  ): Promise<{ originalUrl: string; webUrl?: string }> => {
    // Skip if not a temp URL (might already be finalized or external)
    if (!tempUrl.includes('/character/temp/')) {
      console.log(`⏭️ Skipping non-temp URL: ${tempUrl.substring(0, 50)}...`);
      return { originalUrl: tempUrl };
    }

    try {
      let finalUrl: string;
      let webUrl: string | undefined;

      if (existingBlob) {
        // Use blob directly (fastest, no network call needed for source)
        console.log(`📦 Using provided blob for ${filename}`);
        finalUrl = await uploadCharacterImageToR2(existingBlob, userId, filename, finalPath);

        // Create web-optimized version if requested
        if (createWebVersion) {
          try {
            const webFilename = filename.replace('.png', '_web.jpg');
            const compressedBlob = await compressImageForWeb(existingBlob, 800, 0.75);
            webUrl = await uploadCharacterImageToR2(compressedBlob, userId, webFilename, finalPath);
            console.log(`🌐 Created web version: ${webFilename}`);
          } catch (compressError) {
            console.warn(`⚠️ Failed to create web version for ${filename}:`, compressError);
          }
        }
      } else {
        // No blob available - use S3 CopyObject API (no CORS issues)
        console.log(`📋 Copying ${filename} via S3 API (no blob available)`);
        finalUrl = await copyInR2(tempUrl, finalPath);
        // Can't create web version without blob
      }

      // Track temp URL for deletion
      tempUrlsToDelete.push(tempUrl);

      console.log(`✅ Moved to final: ${filename}`);
      return { originalUrl: finalUrl, webUrl };
    } catch (error) {
      console.error(`❌ Failed to move ${filename} to final:`, error);
      throw new Error('Could not copy a character image to permanent storage. Your original images are kept; please try again.', { cause: error });
    }
  };

  // Move each image type (pass blobs to bypass CORS fetch when available)
  // Create web versions for images that will be displayed frequently
  if (tempUrls.bodyUrl) {
    const result = await moveToFinal(tempUrls.bodyUrl, `body_${Date.now()}.png`, blobs?.bodyBlob, true);
    finalUrls.bodyUrl = result.originalUrl;
    finalUrls.webBodyUrl = result.webUrl;
  }

  if (tempUrls.headUrl) {
    const result = await moveToFinal(tempUrls.headUrl, `head_${Date.now()}.png`, blobs?.headBlob, true);
    finalUrls.headUrl = result.originalUrl;
    finalUrls.webHeadUrl = result.webUrl;
  }

  if (tempUrls.transposedBodyUrl) {
    const result = await moveToFinal(tempUrls.transposedBodyUrl, `transposed_${Date.now()}.png`, blobs?.transposedBodyBlob, true);
    finalUrls.transposedBodyUrl = result.originalUrl;
    finalUrls.webTransposedUrl = result.webUrl;
  }

  if (tempUrls.nsfwUrl) {
    // NSFW version doesn't need web-optimized version (only shown full screen)
    const result = await moveToFinal(tempUrls.nsfwUrl, `nsfw_${Date.now()}.png`, blobs?.nsfwBlob, false);
    finalUrls.nsfwUrl = result.originalUrl;
  }

  if (tempUrls.referenceSheetUrl) {
    // Reference sheet doesn't need web-optimized version (already high quality)
    const result = await moveToFinal(tempUrls.referenceSheetUrl, `refsheet_${Date.now()}.png`, undefined, false);
    finalUrls.referenceSheetUrl = result.originalUrl;
  }

  if (tempUrls.unclothedReferenceSheetUrl) {
    const result = await moveToFinal(tempUrls.unclothedReferenceSheetUrl, `refsheet_unclothed_${Date.now()}.png`, undefined, false);
    finalUrls.unclothedReferenceSheetUrl = result.originalUrl;
  }

  const webCount = [finalUrls.webBodyUrl, finalUrls.webHeadUrl, finalUrls.webTransposedUrl].filter(Boolean).length;
  console.log(`📦 Finalized ${Object.keys(finalUrls).length} images (${webCount} web versions); temp files retained until character is saved`);

  return { ...finalUrls, tempUrlsToDelete: [...new Set(tempUrlsToDelete)] };
}

/** Call only after both passport and character writes succeed. Cleanup does not block navigation. */
export async function cleanupTempCharacterImages(tempUrls: string[]): Promise<void> {
  for (const tempUrl of new Set(tempUrls)) {
    if (!tempUrl.includes('/character/temp/')) continue;
    try {
      await deleteFromR2(tempUrl);
      console.log(`🗑️ Deleted temp: ${tempUrl.substring(tempUrl.lastIndexOf('/') + 1)}`);
    } catch (error) {
      // Log but don't fail - orphaned temps can be cleaned up later
      console.warn(`⚠️ Failed to delete temp image:`, error);
    }
  }

}

/**
 * Upload generated image to R2 and return URL
 * Uses TEMPORARY storage by default during editing
 */
export async function saveGeneratedImageToR2(
  imageUrl: string | undefined,
  imageBlob: Blob | undefined,
  userId: string,
  projectId: string,
  characterId: string,
  type: 'body' | 'head' | 'clothing' | 'accessory',
  isPermanent: boolean = false
): Promise<string> {
  // If we already have a URL (from pollinations or external service), use it
  if (imageUrl) {
    return imageUrl;
  }

  // If we have a blob, upload to R2
  if (imageBlob) {
    const filename = `${type}_${Date.now()}.png`;

    // Use appropriate storage location
    if (isPermanent) {
      return uploadPermanentCharacterImage(imageBlob, userId, projectId, characterId, filename);
    } else {
      return uploadTempCharacterImage(imageBlob, userId, projectId, characterId, filename);
    }
  }

  throw new Error('No image data provided');
}

// ============================================
// CHARACTER OPERATIONS
// ============================================

/**
 * Update just the avatar Y position for a character (lightweight update)
 */
export async function updateAvatarYPosition(characterId: string, yPosition: number): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('characters_2d')
      .update({
        avatar_y_position: yPosition,
        updated_at: Date.now()
      })
      .eq('id', characterId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Failed to update avatar position:', error);
    return false;
  }
}

/**
 * Save a 2D layered character to the database
 */
export async function saveCharacter2D(character: Character2DLayered) {
  try {
    return await retryCharacterWrite(async () => {
      const { data, error } = await supabase
        .from('characters_2d')
        .upsert(
          {
            id: character.id,
            user_id: character.user_id,
            project_id: character.project_id,
            name: character.name,
            description: character.description,
            character_type: character.character_type,
            archetype: character.archetype, // Added: preserve archetype (film, manga_anime, etc.)
            form: character.form, // Added: preserve form (humanoid, creature)
            style_preset: character.style_preset,
            core_layers: character.core_layers,
            current_step: character.current_step,
            completed_steps: character.completed_steps,
            is_finalized: character.is_finalized,
            status: character.is_finalized ? 'published' : 'draft',
            passport_id: character.passport_id,
            passport_generated_at: character.passport_generated_at,
            thumbnail_url: character.thumbnail_url,
            builder_session: character.builder_session || null, // Save builder session for resume capability
            avatar_y_position: (character as any).avatar_y_position ?? 30,
            created_at: character.created_at,
            updated_at: Date.now(),
          },
          { onConflict: 'id' }
        ).abortSignal(AbortSignal.timeout(20_000));

      if (error) throw error;
      return data;
    });
  } catch (error) {
    console.error('Failed to save character:', error);
    throw error;
  }
}

/**
 * Normalize old character data to new Head layer structure
 * Old format: core_layers { body, face, hair } with steps 1-5
 * New format: core_layers { body, head } with steps 1-4
 */
function normalizeCharacterData(data: any): Character2DLayered {
  const normalized = { ...data };

  // Migrate old face/hair layers to head layer
  if (normalized.core_layers) {
    const oldLayers = normalized.core_layers as any;

    // If old format with face/hair instead of head
    if (oldLayers.face && !oldLayers.head) {
      console.log('🔄 Migrating old face/hair layers to head layer');
      // Use face as the head layer (it typically has the more complete portrait)
      normalized.core_layers = {
        body: oldLayers.body,
        head: {
          ...oldLayers.face,
          type: 'head',
          step: 2,
          // Combine descriptions if hair exists
          description: oldLayers.hair?.description
            ? `${oldLayers.face.description || ''} ${oldLayers.hair.description || ''}`.trim()
            : oldLayers.face.description,
        }
      };
    }
  }

  // Normalize step values (old steps 3,4,5 -> new steps 2,3,4)
  if (normalized.current_step && normalized.current_step > 2) {
    // If they were on face (3) or hair (4), they should now be on head (2)
    // If they were on clothing (5), they should now be on clothing (3)
    if (normalized.current_step <= 4) {
      normalized.current_step = 2; // Head
    } else {
      normalized.current_step = 3; // Clothing
    }
  }

  // Normalize completed_steps array
  if (normalized.completed_steps && Array.isArray(normalized.completed_steps)) {
    const oldSteps = normalized.completed_steps as number[];
    const newSteps = new Set<number>();

    for (const step of oldSteps) {
      if (step === 1) newSteps.add(1); // Body stays 1
      if (step === 2 || step === 3 || step === 4) newSteps.add(2); // Face(2), Hair(3,4) -> Head(2)
      if (step === 5) newSteps.add(3); // Clothing -> 3
    }

    normalized.completed_steps = Array.from(newSteps).sort();
  }

  return normalized as Character2DLayered;
}

/**
 * Load a 2D layered character from the database
 */
export async function loadCharacter2D(characterId: string) {
  try {
    const { data, error } = await supabase
      .from('characters_2d')
      .select('*')
      .eq('id', characterId)
      .single();

    if (error) throw error;
    return normalizeCharacterData(data);
  } catch (error) {
    console.error('Failed to load character:', error);
    throw error;
  }
}

/**
 * Get all characters for a user
 */
export async function getUserCharacters2D(userId: string) {
  try {
    const { data, error } = await supabase
      .from('characters_2d')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(normalizeCharacterData);
  } catch (error) {
    console.error('Failed to load user characters:', error);
    throw error;
  }
}

/**
 * Get all characters for a project
 */
export async function getCharactersByProjectId(projectId: string) {
  try {
    const { data, error } = await supabase
      .from('characters_2d')
      .select('*')
      .eq('project_id', projectId)
      .order('updated_at', { ascending: false })
      .limit(50); // Limit to prevent timeout on large projects

    if (error) throw error;
    return (data || []).map(normalizeCharacterData);
  } catch (error) {
    console.error('Failed to load project characters:', error);
    // Return empty array instead of throwing to prevent app crash
    return [];
  }
}

/**
 * Get a specific character by ID
 */
export async function getCharacterById(characterId: string): Promise<Character2DLayered | null> {
  try {
    const { data, error } = await supabase
      .from('characters_2d')
      .select('*')
      .eq('id', characterId)
      .single();

    if (error) throw error;
    return data as Character2DLayered;
  } catch (error) {
    console.error('Failed to load character by ID:', error);
    return null;
  }
}

/**
 * Delete a 2D character
 */
export async function deleteCharacter2D(characterId: string) {
  try {
    const { error } = await supabase
      .from('characters_2d')
      .delete()
      .eq('id', characterId);

    if (error) throw error;
  } catch (error) {
    console.error('Failed to delete character:', error);
    throw error;
  }
}

/**
 * Save a character passport
 */
export async function saveCharacterPassport(passport: CharacterPassport) {
  try {
    return await retryCharacterWrite(async () => {
      const { data, error } = await supabase
        .from('character_passports')
        .upsert(
          {
            id: passport.id,
            character_id: passport.character_id,
            user_id: passport.user_id,
            reference_sheet: passport.reference_sheet,
            text_description: passport.text_description,
            asset_manifest: passport.asset_manifest,
            usage_prompt: passport.usage_prompt,
            character_data: passport.character_data,
            created_at: passport.created_at,
            updated_at: Date.now(),
          },
          { onConflict: 'id' }
        ).abortSignal(AbortSignal.timeout(20_000));

      if (error) throw error;
      return data;
    });
  } catch (error) {
    console.error('Failed to save passport:', error);
    throw error;
  }
}

/**
 * Load a character passport
 */
export async function loadCharacterPassport(passportId: string) {
  try {
    const { data, error } = await supabase
      .from('character_passports')
      .select('*')
      .eq('id', passportId)
      .single();

    if (error) throw error;
    return data as CharacterPassport;
  } catch (error) {
    console.error('Failed to load passport:', error);
    throw error;
  }
}

/**
 * Get passport by character ID
 */
export async function getPassportByCharacterId(characterId: string) {
  try {
    const { data, error } = await supabase
      .from('character_passports')
      .select('*')
      .eq('character_id', characterId)
      .maybeSingle();

    if (error) throw error;
    return data as CharacterPassport | null;
  } catch (error) {
    console.error('Failed to load passport:', error);
    throw error;
  }
}

